/**
 * Welcome Service - سرویس خوش‌آمدگویی
 * این سرویس مسئول ارسال نوتیفیکیشن خوش‌آمدگویی به کاربران جدید است
 */

import { pushService } from '../push/pushService';

class WelcomeService {
  constructor() {
    this.scheduledNotifications = new Map();
  }

  /**
   * Send immediate welcome notification
   * ارسال فوری نوتیفیکیشن خوش‌آمدگویی
   * 
   * @param {string} userName - نام کاربر
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async sendImmediate(userName = 'کاربر عزیز', userId = null) {
    try {
      console.log('🎉 Sending immediate welcome notification...');
      
      const result = await pushService.showLocalNotification(
        `🎉 خوش آمدید، ${userName}!`,
        {
          body: 'ما اینجا هستیم تا به شما در مراقبت از پوستتان کمک کنیم. بیایید شروع کنیم!',
          data: {
            type: 'welcome',
            url: '/scanpage',
            userId: userId,
            timestamp: Date.now(),
          },
          actions: [
            { action: 'start-scan', title: 'شروع اسکن پوست', icon: '/logo_pwa.png' },
            { action: 'dismiss', title: 'بستن' },
          ],
        },
        'welcome'
      );
      
      if (result) {
        console.log('✅ Immediate welcome notification sent');
        this.trackNotification('immediate', userId);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Failed to send immediate welcome notification:', error);
      return false;
    }
  }

  /**
   * Schedule welcome notification
   * برنامه‌ریزی نوتیفیکیشن خوش‌آمدگویی
   * 
   * @param {string} userName - نام کاربر
   * @param {number} delay - تأخیر به میلی‌ثانیه (پیش‌فرض: 10 ثانیه)
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<number>} - شناسه notification برنامه‌ریزی شده
   */
  async schedule(userName = 'کاربر عزیز', delay = 10000, userId = null) {
    try {
      console.log(`⏰ Scheduling welcome notification for ${delay}ms...`);
      
      const timeoutId = await pushService.scheduleNotification(
        `🎉 به درمووایز خوش آمدید، ${userName}!`,
        {
          body: 'ما اینجا هستیم تا به شما در مراقبت از پوستتان کمک کنیم. بیایید شروع کنیم!',
          data: {
            type: 'welcome',
            url: '/scanpage',
            userId: userId,
            timestamp: Date.now(),
          },
          actions: [
            { action: 'start-scan', title: 'شروع اسکن پوست', icon: '/logo_pwa.png' },
            { action: 'dismiss', title: 'بستن' },
          ],
        },
        delay,
        'welcome'
      );
      
      if (timeoutId) {
        this.scheduledNotifications.set(timeoutId, {
          type: 'welcome',
          userName,
          userId,
          scheduledAt: Date.now(),
        });
        console.log(`✅ Welcome notification scheduled with ID: ${timeoutId}`);
      }
      
      return timeoutId;
    } catch (error) {
      console.error('❌ Failed to schedule welcome notification:', error);
      return null;
    }
  }

  /**
   * Cancel scheduled welcome notification
   * لغو نوتیفیکیشن خوش‌آمدگویی برنامه‌ریزی شده
   * 
   * @param {number} notificationId - شناسه notification
   * @returns {boolean}
   */
  cancel(notificationId) {
    try {
      if (this.scheduledNotifications.has(notificationId)) {
        pushService.cancelScheduledNotification(notificationId);
        this.scheduledNotifications.delete(notificationId);
        console.log(`✅ Welcome notification cancelled: ${notificationId}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to cancel welcome notification:', error);
      return false;
    }
  }

  /**
   * Cancel all scheduled welcome notifications
   * لغو همه نوتیفیکیشن‌های خوش‌آمدگویی برنامه‌ریزی شده
   * 
   * @returns {number} - تعداد نوتیفیکیشن‌های لغو شده
   */
  cancelAll() {
    try {
      let count = 0;
      for (const [notificationId] of this.scheduledNotifications) {
        if (this.cancel(notificationId)) {
          count++;
        }
      }
      console.log(`✅ Cancelled ${count} welcome notifications`);
      return count;
    } catch (error) {
      console.error('❌ Failed to cancel all welcome notifications:', error);
      return 0;
    }
  }

  /**
   * Track notification sent
   * ثبت نوتیفیکیشن ارسال شده
   * 
   * @param {string} type - نوع ارسال (immediate, scheduled)
   * @param {string} userId - شناسه کاربر
   */
  trackNotification(type, userId) {
    try {
      const key = `welcome_notification_${type}`;
      const data = {
        userId,
        sentAt: Date.now(),
        type,
      };
      localStorage.setItem(key, JSON.stringify(data));
      console.log(`📊 Tracked welcome notification: ${type}`);
    } catch (error) {
      console.error('❌ Failed to track notification:', error);
    }
  }

  /**
   * Check if welcome notification was already sent
   * بررسی ارسال قبلی نوتیفیکیشن خوش‌آمدگویی
   * 
   * @param {string} type - نوع ارسال (immediate, scheduled)
   * @returns {boolean}
   */
  wasAlreadySent(type = 'immediate') {
    try {
      const key = `welcome_notification_${type}`;
      const data = localStorage.getItem(key);
      return data !== null;
    } catch (error) {
      console.error('❌ Failed to check if notification was sent:', error);
      return false;
    }
  }
}

// Export singleton instance
export const welcomeService = new WelcomeService();

export default welcomeService;



